import { TrustOffers } from "./partners/Trust.jsx";
import { useState } from "react";
import { money, percent, day, dateTime, cabDateTime } from "../api.js";
import { useResource, isStale } from "./useResource.js";
import { monthShort } from "./period.js";
import { DownloadButton, STATE } from "./Reports.jsx";
import { ACTIVE, shortName } from "./common.jsx";
import Money from "../components/Money.jsx";
import { riskyNow } from "../../../lib/compliance/smkor-plan.js";

// «Огляд»: за кілька секунд — який стан компанії, що потребує уваги, що зробити далі.
// Кожен блок має власний ресурс і власні стани; збій одного не ховає решту.

const n = (v) => (v == null ? null : Number(v));
const uah = (v) => (v == null ? "—" : `${money(v)} грн`);
const plural = (k, one, few, many) => {
  const a = Math.abs(k) % 100, b = a % 10;
  return a > 10 && a < 20 ? many : b === 1 ? one : b >= 2 && b <= 4 ? few : many;
};
const SECTION_TITLE = { erpn: "ПН/РК", card: "Дані компанії", budget: "Розрахунки", sea: "СЕА", smkor: "СМКОР", prro: "Каси й продажі", reports: "Звітність" };

export default function Overview(props) {
  const { companyId, company, overview, overviewError, resources, versions, period, busy, canSync, onSyncAll, onRelogin } = props;
  const base = `/api/companies/${companyId}`;
  const r = {
    erpn: useResource(`${base}/dashboard/erpn?from=${period.from}&to=${period.to}`, versions.erpn),
    docs: useResource(`${base}/invoices?page=0`, versions.erpn),
    budget: useResource(`${base}/budget`, versions.budget),
    sea: useResource(`${base}/sea`, versions.sea),
    smkor: useResource(`${base}/smkor`, versions.smkor),
    reports: useResource(`${base}/reports`, versions.reports),
    stock: useResource(`${base}/dashboard/stock`, `${versions.erpn || 0}:${versions.sea || 0}`),
  };
  const link = (k) => `#/c/${companyId}/${k}`;
  const items = attention({ ...props, r, link });
  const latestReceipt = latestSubmitted(r.reports.data?.reports).find((x) => x.last_receipt_id);

  return (
    <div className="dash">
      {company && <TrustOffers companyId={companyId} company={company} />}
      {/* Головне — числа: показники першими, далі «Потребує уваги» і швидкі дії */}
      <section aria-labelledby="kpi-h">
        <h2 id="kpi-h" className="sr-only">Ключові показники</h2>
        <div className="kpis">
          <InvoiceKpi title="Видані ПН/РК" dir="out" r={r.erpn} period={period} res={resources.erpn} />
          <InvoiceKpi title="Отримані ПН/РК" dir="in" r={r.erpn} period={period} res={resources.erpn} />
          <LimitKpi r={r.sea} res={resources.sea} />
          <BudgetKpi r={r.budget} res={resources.budget} />
        </div>
      </section>
      <div className="dash-top">
        <Attention items={items} checking={Object.values(r).some((x) => x.pending) || (!overview && !overviewError)} link={link} />
        <section className="card dash-actions" aria-labelledby="actions-h">
          <h2 id="actions-h" className="block-title">Швидкі дії</h2>
          <div className="action-list">
            {/* Синхронізація — кнопкою «Синхронізувати все» в шапці; тут — запит бізнес-партнера */}
            {canSync
              ? <a className="btn btn-block" href={link("partners/partnership")}>Запит бізнес-партнера</a>
              : <button type="button" className="btn btn-block" onClick={onRelogin}>Увійти ключем КЕП</button>}
            <a className="btn btn-secondary btn-block" href={link("erpn")}>Переглянути ПН/РК</a>
            <a className="btn btn-secondary btn-block" href={link("reports")}>Звітність і квитанції</a>
            {latestReceipt && (
              <DownloadButton path={`${base}/receipts/${latestReceipt.last_receipt_id}/pdf`} block>
                Остання квитанція · {latestReceipt.cdoc}
              </DownloadButton>
            )}
          </div>
        </section>
      </div>

      <Trend r={r.erpn} smkor={r.smkor} />
      <StockBlock r={r.stock} res={resources.erpn} />
      <Crit1Block r={r.stock} res={resources.erpn} customsHref={link("customs")} />

      <div className="dash-grid">
        <DocsBlock r={r.docs} res={resources.erpn} href={link("erpn")} />
        <ReportsBlock r={r.reports} res={resources.reports} href={link("reports")} base={base} />
        <BudgetBlock r={r.budget} res={resources.budget} href={link("budget")} />
        <SeaBlock r={r.sea} res={resources.sea} href={link("sea")} />
        <SmkorBlock r={r.smkor} res={resources.smkor} href={link("smkor")} />
      </div>
    </div>
  );
}

// --- «Потребує уваги» ---------------------------------------------------------

function attention({ r, resources, overview, overviewError, canSync, onRelogin, onSyncAll, link }) {
  const out = [];
  if (!canSync || Object.values(resources).some((x) => x.lastJob?.status === "needs_login")) {
    out.push({ tone: "bad", key: "login", title: "Сесія ДПС завершилась",
      detail: "Без входу ключем КЕП дані не оновлюються. Ключ і пароль лишаються у браузері.",
      action: { label: "Увійти ключем КЕП", onClick: onRelogin } });
  }
  const stopped = r.smkor.data?.stopped?.data;
  if (Array.isArray(stopped)) {
    const st = stopped.filter((x) => x.status === 1);
    if (st.length) {
      const noExpl = st.filter((x) => !(x.det || []).some((d) => d.hdate)).length;
      out.push({ tone: "bad", key: "stopped", title: `Зупинено ${st.length} ${plural(st.length, "ПН/РК", "ПН/РК", "ПН/РК")}`,
        detail: `ПДВ ${uah(st.reduce((a, x) => a + (Number(x.vat) || 0), 0))}${noExpl ? ` · без поданих пояснень: ${noExpl}` : ""}`,
        href: link("smkor"), linkText: "До СМКОР" });
    }
  }
  const year = r.budget.data?.years?.[0];
  if (Array.isArray(year?.rows)) {
    const debt = year.rows.filter((x) => Number(x.nedoimEnd) > 0);
    const pen = year.rows.reduce((a, x) => a + (Number(x.peniaEnd) || 0), 0);
    if (debt.length) {
      out.push({ tone: "bad", key: "debt", title: `Податковий борг ${uah(debt.reduce((a, x) => a + Number(x.nedoimEnd), 0))}`,
        detail: `${debt.length} ${plural(debt.length, "платіж", "платежі", "платежів")}: ${debt.map((x) => (x.namePlt || "").trim().slice(0, 60)).join("; ")}${pen > 0 ? ` · пеня ${uah(pen)}` : ""}`,
        href: link("budget"), linkText: "До розрахунків" });
    }
  }
  const reps = r.reports.data?.reports;
  if (Array.isArray(reps)) {
    // Невиправлені відхилення: групуємо спроби однієї форми за один період
    const groups = new Map();
    for (const x of reps.filter((y) => y.state === "error" && !y.superseded)) {
      const k = `${String(x.cdoc).slice(0, 6)}|${x.period_year}|${x.period_month}|${x.period_type}`;
      const g = groups.get(k) || { cdoc: x.cdoc, period: [x.period_name, x.period_year].filter(Boolean).join(" "), n: 0, last: "" };
      g.n++; if (String(x.oper_date) > g.last) g.last = String(x.oper_date);
      groups.set(k, g);
    }
    const gs = [...groups.values()].sort((a, b) => b.last.localeCompare(a.last));
    if (gs.length) {
      out.push({ tone: "bad", key: "rejected", title: `Звітність не прийнято: ${gs.length} ${plural(gs.length, "форма", "форми", "форм")} без прийнятого подання`,
        detail: gs.slice(0, 3).map((g) => `${g.cdoc} за ${g.period}${g.n > 1 ? ` (${g.n} ${plural(g.n, "спроба", "спроби", "спроб")})` : ""}, останнє ${cabDateTime(g.last)}`).join("; ") + (gs.length > 3 ? " та інші" : ""),
        href: link("reports"), linkText: "До звітності" });
    }
  }
  // ПН без операції СЕА важливі лише з ПДВ: нульові (напр. від неплатників ПДВ) на ліміт не впливають
  const nis = r.sea.data?.notInSea?.filter((x) => Number(x.vat_sum) !== 0);
  if (Array.isArray(nis) && nis.length) {
    out.push({ tone: "warn", key: "sea", title: `${nis.length} ПН/РК з ПДВ немає в реєстрі операцій СЕА`,
      detail: `ПДВ у них ${uah(nis.reduce((a, x) => a + (Number(x.vat_sum) || 0), 0))}: ліміт реєстрації може бути недоврахований — перевірте операції в кабінеті.`,
      href: link("sea"), linkText: "До СЕА" });
  }
  // МД з реєстру СЕА, не завантажені в «Митні декларації»: ПДВ відомий, а товарів для руху й залишків немає
  const mdMiss = r.sea.data?.customsMissing;
  if (Array.isArray(mdMiss) && mdMiss.length) {
    out.push({ tone: "warn", key: "md", title: `Завантажте митні декларації: ${mdMiss.length} є в СЕА, але не в «Митних деклараціях»`,
      detail: `${mdMiss.slice(0, 3).map((m) => `${m.docnum} від ${day(m.docdt)}`).join("; ")}${mdMiss.length > 3 ? " та інші" : ""} · ПДВ ${uah(mdMiss.reduce((a, m) => a + (Number(m.vat) || 0), 0))}. Без файлу МД рух товарів і залишки неповні.`,
      href: link("customs"), linkText: "До митних декларацій" });
  }
  if (overviewError) out.push({ tone: "warn", key: "ovr", title: "Не вдалося отримати стан синхронізації", detail: overviewError.message });
  const stale = [];
  for (const x of Object.values(resources)) {
    const j = x.lastJob, title = SECTION_TITLE[x.key];
    if (!j || ACTIVE.includes(j.status) || j.status === "needs_login") continue;
    if (j.status === "failed" || j.status === "unknown_result") {
      out.push({ tone: "warn", key: `fail-${x.key}`, title: `Розділ «${title}» не оновився`,
        detail: `${j.error || "Невідома помилка"}${x.lastSuccessAt ? `. Показано дані на ${dateTime(x.lastSuccessAt)}` : ""}`,
        href: link(x.key), linkText: "Відкрити" });
    } else if (x.partialErrors?.length) {
      out.push({ tone: "warn", key: `part-${x.key}`, title: `Розділ «${title}» оновлено частково`,
        detail: `${x.partialErrors.length} ${plural(x.partialErrors.length, "помилка", "помилки", "помилок")}: ${x.partialErrors[0].message.slice(0, 120)}`,
        href: link(x.key), linkText: "Відкрити" });
    } else if (isStale(x)) stale.push(x);
  }
  // Застарілі розділи — один пункт і одна кнопка: синк однаково оновлює всі розділи разом
  if (stale.length) {
    const oldest = stale.reduce((a, x) => (!a || x.lastSuccessAt < a ? x.lastSuccessAt : a), null);
    out.push({ tone: "warn", key: "stale", title: stale.length === 1 ? `Дані «${SECTION_TITLE[stale[0].key]}» застаріли` : `Дані застаріли: ${stale.map((x) => SECTION_TITLE[x.key]).join(", ")}`,
      detail: `${stale.length === 1 ? "Останнє оновлення" : "Найдавніше оновлення"} ${dateTime(oldest)}`, action: canSync ? { label: "Синхронізувати все", onClick: onSyncAll } : null });
  }
  if (overview && overview.resources.every((x) => !x.lastJob)) {
    out.push({ tone: "warn", key: "never", title: "Розділи ще не синхронізовано", detail: "Дані підтягнуться з кабінету ДПС за вашою сесією.",
      action: canSync ? { label: "Синхронізувати", onClick: onSyncAll } : null });
  }
  for (const [k, title] of [["budget", "розрахунки"], ["sea", "СЕА"], ["smkor", "СМКОР"], ["reports", "звітність"], ["erpn", "показники ЄРПН"]]) {
    if (r[k].error && r[k].data === undefined) out.push({ tone: "warn", key: `load-${k}`, title: `Не вдалося завантажити ${title}`, detail: r[k].error.message });
  }
  return out.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === "bad" ? -1 : 1));
}

function Attention({ items, checking }) {
  return (
    <section className="card attention" aria-labelledby="att-h">
      <h2 id="att-h" className="block-title">Потребує уваги{items.length ? <span className="count">{items.length}</span> : null}</h2>
      {items.length ? (
        <ul className="att-list">
          {items.map((it) => (
            <li key={it.key} className={`att att-${it.tone}`}>
              <span className="att-mark" aria-hidden="true" />
              <div className="att-body">
                <p className="att-title"><span className="sr-only">{it.tone === "bad" ? "Важливо: " : "Перевірити: "}</span>{it.title}</p>
                {it.detail && <p className="att-detail">{it.detail}</p>}
              </div>
              {it.action ? <button type="button" className="btn btn-sm" onClick={it.action.onClick}>{it.action.label}</button>
                : it.href ? <a className="att-link" href={it.href}>{it.linkText || "Відкрити"}<span aria-hidden="true"> →</span></a> : null}
            </li>
          ))}
        </ul>
      ) : checking ? (
        <p className="calm muted" role="status">Перевіряємо дані…</p>
      ) : (
        <p className="calm"><span className="status-dot" aria-hidden="true" />Нічого термінового: сесія ДПС діє, зупинених ПН і боргу немає, звітність прийнята, синк без помилок.</p>
      )}
      {checking && items.length > 0 && <p className="muted small" role="status">Перевіряємо решту даних…</p>}
    </section>
  );
}

// --- Показники ------------------------------------------------------------------

function Meta({ children }) {
  return <p className="kpi-meta">{children}</p>;
}

function Kpi({ label, r, children, meta, empty }) {
  return (
    <div className="kpi" aria-busy={r.loading}>
      <p className="kpi-label">{label}</p>
      {r.pending ? <p className="kpi-value skeleton" role="status"><span className="sr-only">Завантаження…</span></p>
        : r.error && r.data === undefined ? <p className="kpi-empty error">Не вдалося завантажити</p>
        : empty ? <p className="kpi-empty">{empty}</p>
        : children}
      {meta && <Meta>{meta}</Meta>}
    </div>
  );
}

function InvoiceKpi({ title, dir, r, period, res }) {
  const d = r.data;
  const cur = d?.current?.[dir], prev = d?.previous?.[dir];
  const count = cur ? cur.pn + cur.rk : null;
  return (
    <Kpi label={title} r={r} empty={d && !d.synced ? "ЄРПН ще не синхронізовано" : null}
      meta={<>{period.label} · ЄРПН, за датою складання{isStale(res) ? " · дані застаріли" : ""}</>}>
      {cur && (
        <>
          <p className="kpi-value">{count}<span className="kpi-unit"> {plural(count, "документ", "документи", "документів")}</span></p>
          <p className="kpi-sub">ПДВ у документах {uah(cur.vat)}{cur.rk ? ` · з них РК: ${cur.rk}` : ""}</p>
          {dir === "in" && Number(d.current?.customs?.vat) ? <p className="kpi-sub">+ імпорт за МД ({d.current.customs.n}): ПДВ {uah(d.current.customs.vat)}</p> : null}
          {prev ? <p className="kpi-cmp">Попередній період: {prev.pn + prev.rk} док., ПДВ {uah(prev.vat)}</p>
            : d.notComparableReason ? <p className="kpi-cmp muted">Без порівняння: {d.notComparableReason}</p> : null}
        </>
      )}
    </Kpi>
  );
}

function LimitKpi({ r, res }) {
  const ops = r.data?.operations;
  const limit = ops?.length ? n(ops[0].sumlm) : null;
  return (
    <Kpi label="Ліміт реєстрації ПН/РК" r={r} empty={r.data && limit == null ? "Даних СЕА немає" : null}
      meta={<>Поточний залишок · СЕА{res?.lastSuccessAt ? `, на ${dateTime(res.lastSuccessAt)}` : ""}</>}>
      {limit != null && (
        <>
          <p className="kpi-value"><Money value={limit} /><span className="kpi-unit"> грн</span></p>
          <p className="kpi-sub">Після операції {cabDateTime(ops[0].opdt)}</p>
        </>
      )}
    </Kpi>
  );
}

function BudgetKpi({ r, res }) {
  const y = r.data?.years?.[0];
  const rows = y?.rows;
  const debt = Array.isArray(rows) ? rows.reduce((a, x) => a + (Number(x.nedoimEnd) || 0), 0) : null;
  const over = Array.isArray(rows) ? rows.reduce((a, x) => a + (Number(x.pereplEnd) || 0), 0) : null;
  return (
    <Kpi label={`Розрахунки з бюджетом, ${y?.year ?? ""}`} r={r} empty={r.data && !Array.isArray(rows) ? "Даних про розрахунки немає" : null}
      meta={<>Станом на {y?.fetchedAt ? dateTime(y.fetchedAt) : "—"}{y?.source === "official" ? " · офіційний API ДПС" : ""}</>}>
      {Array.isArray(rows) && (
        <div className="kpi-pair">
          <div><span className="kpi-sub">Борг</span><p className={`kpi-value kpi-value-sm${debt > 0 ? " error" : ""}`}><Money value={debt} /><span className="kpi-unit"> грн</span></p></div>
          <div><span className="kpi-sub">Переплата</span><p className="kpi-value kpi-value-sm"><Money value={over} /><span className="kpi-unit"> грн</span></p></div>
        </div>
      )}
    </Kpi>
  );
}

// --- Динаміка ---------------------------------------------------------------------

// Рівні шкали: «круглий» крок (1, 2, 2,5, 5 × 10ⁿ), щоб на осі було 3–5 позначок
function niceTicks(max) {
  const raw = max / 4, p = 10 ** Math.floor(Math.log10(raw || 1));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * p).find((x) => x >= raw) || 10 * p;
  const top = Math.max(step, Math.ceil(max / step) * step);
  const ticks = [];
  for (let v = 0; v <= top + 1e-6; v += step) ticks.push(v);
  return { top, ticks };
}
const compact = (v) => (v >= 1e6 ? `${(v / 1e6).toLocaleString("uk-UA", { maximumFractionDigits: 1 })} млн`
  : v >= 1e3 ? `${(v / 1e3).toLocaleString("uk-UA", { maximumFractionDigits: 0 })} тис.` : v.toLocaleString("uk-UA"));

// ПДВ по місяцях: видані, отримані ПН/РК і імпорт за МД (стовпчики) + Рмакс (пунктир над «Видані»; з плану СМКОР —
// межа ПДВ прийнятих на реєстрацію ПН за місяць для автоматичної реєстрації, та сама гривнева вісь). D — відсоток, тому
// окремим показником у шапці, а не на осі гривень
function Trend({ r, smkor }) {
  const rows = r.data?.monthly;
  if (!Array.isArray(rows)) return null;
  const months = [...new Set(rows.map((x) => x.month))].sort();
  if (months.length < 2) return null;
  const get = (m, d) => Number(rows.find((x) => x.month === m && x.direction === d)?.vat ?? 0);
  // Вхідний ПДВ = отримані ПН/РК + імпорт за МД (з реєстру СЕА — лише МД, оформлені на компанію)
  const hasMd = rows.some((x) => x.direction === "customs" && Number(x.vat));
  const pm = Object.fromEntries((smkor?.data?.plan?.pmax?.months || []).filter((x) => months.includes(x.month))
    .map((x) => [x.month, { v: x.pmax ?? x.pmaxLow, lowOnly: x.pmax == null, verdict: x.verdict, accepted: x.accepted }]).filter(([, x]) => x.v > 0));
  const hasPm = Object.keys(pm).length > 0;
  const dp = smkor?.data?.dp?.data;
  const peak = Math.max(1, ...months.flatMap((m) => [get(m, "out"), get(m, "in") + Math.max(0, get(m, "customs")), pm[m]?.v || 0]));
  const { top, ticks } = niceTicks(peak);
  const W = 100 / months.length, H = 40, y = (v) => H - (v / top) * H;
  const h = (v) => (v / top) * H;
  return (
    <section className="card trend" aria-labelledby="trend-h">
      <div className="row trend-head">
        <h2 id="trend-h" className="block-title">ПДВ по місяцях: ПН/РК{hasMd ? " і митні декларації" : ""}</h2>
        {dp && (dp.D != null || dp.PM != null) && (
          <dl className="trend-stats">
            {dp.D != null && <div><dt>D — податкове навантаження</dt><dd>{percent(dp.D)}</dd></div>}
            {dp.PM != null && <div><dt>Рнайб за рік</dt><dd>{money(dp.PM)} грн</dd></div>}
          </dl>)}
      </div>
      <p className="legend"><span className="lg lg-out" />Видані<span className="lg lg-in" />Отримані{hasMd && <><span className="lg lg-md" />МД (імпорт)</>}
        {hasPm && <><span className="lg lg-pmax" aria-hidden="true" />Рмакс — межа ПДВ ПН, прийнятих на реєстрацію за місяць</>}</p>
      <div className="trend-plot">
        <div className="trend-y" aria-hidden="true">{ticks.map((t) => <span key={t} style={{ bottom: `${(t / top) * 100}%` }}>{compact(t)}</span>)}</div>
        <svg className="bars" viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" role="img"
          aria-label={`ПДВ у виданих і отриманих ПН/РК${hasMd ? " та за митними деклараціями" : ""}${hasPm ? " і межа Рмакс" : ""} за ${months.length} місяців. Деталі — у таблиці нижче.`}>
          {ticks.map((t) => <line key={t} className="grid" x1="0" x2="100" y1={y(t)} y2={y(t)} vectorEffect="non-scaling-stroke" />)}
          {months.map((m, i) => {
            const o = get(m, "out"), inn = get(m, "in"), md = Math.max(0, get(m, "customs")), p = pm[m];
            return (
              <g key={m}>
                <rect className="bar-out" x={i * W + W * 0.18} y={y(o)} width={W * 0.3} height={h(o)}><title>{`${monthShort(m)}: видані ${money(o)} грн`}</title></rect>
                <rect className="bar-in" x={i * W + W * 0.52} y={y(inn)} width={W * 0.3} height={h(inn)}><title>{`${monthShort(m)}: отримані ПН/РК ${money(inn)} грн`}</title></rect>
                {md > 0 && <rect className="bar-md" x={i * W + W * 0.52} y={y(inn + md)} width={W * 0.3} height={h(md)}><title>{`${monthShort(m)}: МД (імпорт) ${money(md)} грн · разом вхідний ПДВ ${money(inn + md)} грн`}</title></rect>}
                {p && <g className={p.verdict === "over" ? "pmax pmax-over" : "pmax"}>
                  <line x1={i * W + W * 0.06} x2={i * W + W * 0.94} y1={y(p.v)} y2={y(p.v)} vectorEffect="non-scaling-stroke" />
                  <rect className="hit" x={i * W} y={Math.max(0, y(p.v) - 2)} width={W} height="4"><title>{`${monthShort(m)}: Рмакс ${p.lowOnly ? "не менше " : ""}${money(p.v)} грн · прийнято на реєстрацію ${money(p.accepted)} грн${p.verdict === "over" ? " — перевищує" : p.verdict === "ok" ? " — у межах" : ""}`}</title></rect>
                </g>}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="bars-axis trend-x" aria-hidden="true">{months.map((m) => <span key={m}>{monthShort(m)}</span>)}</div>
      <div className="sr-only"><table>
        <caption>ПДВ по місяцях, грн</caption>
        <thead><tr><th>Місяць</th><th>Видані</th><th>Отримані ПН/РК</th>{hasMd && <th>МД (імпорт)</th>}{hasPm && <th>Рмакс</th>}</tr></thead>
        <tbody>{months.map((m) => <tr key={m}><td>{monthShort(m)}</td><td>{money(get(m, "out"))}</td><td>{money(get(m, "in"))}</td>{hasMd && <td>{money(get(m, "customs"))}</td>}{hasPm && <td>{pm[m] ? `${pm[m].lowOnly ? "не менше " : ""}${money(pm[m].v)}` : "—"}</td>}</tr>)}</tbody>
      </table></div>
    </section>
  );
}

// --- Робочі блоки -----------------------------------------------------------------

function Block({ title, id, r, res, href, children, emptyText, isEmpty }) {
  return (
    <section className="card block" aria-labelledby={id} aria-busy={r.loading}>
      <div className="block-head">
        <h2 id={id} className="block-title">{title}</h2>
        {href && <a href={href} className="block-link">Переглянути всі<span aria-hidden="true"> →</span></a>}
      </div>
      <BlockState r={r} res={res} />
      {r.pending ? <div className="skeleton-lines" role="status"><span /><span /><span /><span className="sr-only">Завантаження…</span></div>
        : r.error && r.data === undefined ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
        : isEmpty ? <p className="empty">{emptyText}</p>
        : children}
    </section>
  );
}

function BlockState({ r, res }) {
  const j = res?.lastJob;
  let text = null, tone = "";
  if (ACTIVE.includes(j?.status) || r.updating) { text = "Оновлюється…"; tone = "busy"; }
  else if (j?.status === "failed") { text = `Не оновився${res.lastSuccessAt ? `, дані на ${dateTime(res.lastSuccessAt)}` : ""}`; tone = "bad"; }
  else if (isStale(res)) { text = `Застаріло: оновлено ${dateTime(res.lastSuccessAt)}`; tone = "warn"; }
  else if (res?.lastSuccessAt) text = `Оновлено ${dateTime(res.lastSuccessAt)}`;
  if (r.error && r.data !== undefined) { text = `Не вдалося оновити: ${r.error.message}`; tone = "bad"; }
  return text ? <p className={`block-state status-${tone || "ok"}`}><span className="status-dot" aria-hidden="true" />{text}</p> : null;
}

const STATUS_TONE = (s) => (/зареєстр/i.test(s || "") ? "ok" : /зупин|відмов/i.test(s || "") ? "bad" : "idle");

function DocsBlock({ r, res, href }) {
  const list = r.data?.invoices?.slice(0, 6) || [];
  return (
    <Block title="Останні ПН/РК" id="docs-h" r={r} res={res} href={href} isEmpty={r.data && !list.length} emptyText="Документів ще немає.">
      <ul className="docs">
        {list.map((i) => (
          <li key={i.code} className="doc">
            <span className="doc-party" title={(i.direction === "out" ? i.buyer : i.seller) || ""}>{shortName(i.direction === "out" ? i.buyer : i.seller) || "—"}</span>
            <span className="doc-meta muted">{i.direction === "out" ? "Видана" : "Отримана"} {i.doc_kind || "ПН"} № {i.nmr} · {day(i.crtdate)}</span>
            <span className="doc-sum num">{uah(i.vat_sum)}<span className="muted"> ПДВ</span></span>
            <span className={`badge badge-${STATUS_TONE(i.status_name)}`}>{i.status_name || "—"}</span>
          </li>
        ))}
      </ul>
    </Block>
  );
}

function BudgetBlock({ r, res, href }) {
  const y = r.data?.years?.[0];
  const rows = Array.isArray(y?.rows) ? y.rows : null;
  const shown = rows ? [...rows].filter((x) => Number(x.nedoimEnd) || Number(x.pereplEnd))
    .sort((a, b) => Number(b.nedoimEnd) - Number(a.nedoimEnd) || Number(b.pereplEnd) - Number(a.pereplEnd)).slice(0, 5) : [];
  return (
    <Block title={`Розрахунки з бюджетом${y ? `, ${y.year}` : ""}`} id="budget-h" r={r} res={res} href={href}
      isEmpty={r.data && !rows} emptyText="Даних про розрахунки ще немає.">
      {rows && (shown.length ? (
        <table className="mini">
          <thead><tr><th>Платіж</th><th className="num">Борг</th><th className="num">Переплата</th></tr></thead>
          <tbody>
            {shown.map((x, i) => (
              <tr key={x.idpmnt ?? i}>
                <td><span className="clamp">{(x.namePlt || "").trim()}</span><span className="muted small">{x.nameSti}</span></td>
                <td className={`num${Number(x.nedoimEnd) > 0 ? " error" : ""}`}>{money(x.nedoimEnd)}</td>
                <td className="num">{money(x.pereplEnd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="empty">Боргу й переплати немає за жодним із {rows.length} {plural(rows.length, "платежу", "платежів", "платежів")}.</p>)}
    </Block>
  );
}

function SeaBlock({ r, res, href }) {
  const d = r.data;
  const ops = d?.operations || [];
  const trans = [...(d?.transactions || [])].sort((a, b) => String(b.pmtDate).localeCompare(String(a.pmtDate)));
  return (
    <Block title="СЕА ПДВ" id="sea-h" r={r} res={res} href={href} isEmpty={d && !ops.length && !trans.length} emptyText="Даних СЕА ще немає.">
      <dl className="facts">
        <div><dt>Ліміт реєстрації</dt><dd>{ops.length ? uah(ops[0].sumlm) : "—"}</dd></div>
        <div><dt>Залишок на рахунку</dt><dd>{trans.length ? uah(trans[0].balance) : "—"}</dd></div>
        <div><dt>Операцій у реєстрі</dt><dd>{ops.length}</dd></div>
        <div><dt>ПН/РК без операції СЕА</dt><dd>{d?.invoicesNotInSea ?? "—"}</dd></div>
      </dl>
      {d?.notInSea?.length > 0 && (() => {
        const withVat = d.notInSea.filter((x) => Number(x.vat_sum) !== 0);
        return withVat.length
          ? <p className="small warn-text">З них з ПДВ: {withVat.length} на {uah(withVat.reduce((a, x) => a + Number(x.vat_sum), 0))} — ліміт може бути недоврахований.</p>
          : <p className="small muted">Усі з нульовим ПДВ — на ліміт реєстрації не впливають.</p>;
      })()}
      {d?.components?.length > 0 && (
        <>
          <h3 className="mini-title">Складові ліміту</h3>
          <ul className="components">
            {d.components.slice(0, 4).map((c) => (
              <li key={c.optype}><span className="clamp">{c.name}</span><span className="num">{money(c.amount)}</span></li>
            ))}
          </ul>
        </>
      )}
    </Block>
  );
}

function latestSubmitted(list) {
  return (list || []).filter((x) => (x.kvt ?? 0) > 0).sort((a, b) => String(b.oper_date).localeCompare(String(a.oper_date)));
}

function ReportsBlock({ r, res, href, base }) {
  const list = latestSubmitted(r.data?.reports).slice(0, 5);
  return (
    <Block title="Звітність: останні подання" id="rep-h" r={r} res={res} href={href} isEmpty={r.data && !list.length} emptyText="Поданої звітності ще немає.">
      <ul className="reps">
        {list.map((x) => {
          const [label, tone] = STATE[x.state] || [x.state, "idle"];
          return (
            <li key={x.id} className="rep">
              <div className="rep-main">
                <span><b>{x.cdoc}</b> <span className="clamp-inline">{x.doc_name}</span></span>
                <span className="muted small">{[x.period_name, x.period_year].filter(Boolean).join(" ")} · подано {cabDateTime(x.oper_date)}</span>
                {x.last_receipt && <span className="muted small clamp">{x.last_receipt.split(/\r?\n/)[0]}</span>}
              </div>
              <div className="rep-side">
                <span className={`badge badge-${tone}`}>{label}{x.state === "error" && x.superseded ? " · виправлено" : ""}</span>
                {x.last_receipt_id && <DownloadButton path={`${base}/receipts/${x.last_receipt_id}/pdf`} small>PDF</DownloadButton>}
              </div>
            </li>
          );
        })}
      </ul>
    </Block>
  );
}

function SmkorBlock({ r, res, href }) {
  const d = r.data;
  const risk = d?.risk?.data, dp = d?.dp?.data, ppi = d?.ppi?.data, stopped = d?.stopped?.data;
  const last = Array.isArray(ppi) && ppi.length ? [...ppi].sort((a, b) => String(b.rdate).localeCompare(String(a.rdate)))[0] : null;
  const marks = last ? Array.from({ length: 9 }, (_, i) => i + 1).filter((k) => last[`p${k}`] === 1) : [];
  const empty = d && !risk && !dp && !ppi && !stopped;
  return (
    <Block title="СМКОР" id="smkor-h" r={r} res={res} href={href} isEmpty={empty} emptyText="Даних СМКОР ще немає.">
      <dl className="facts">
        <div><dt>Ризиковість</dt><dd>{!Array.isArray(risk) ? "—" : riskyNow(risk, new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Kyiv" })) ? <span className="error">Відповідає критеріям</span> : <span className="ok-text">Не ризиковий</span>}</dd></div>
        <div><dt>D, податкове навантаження</dt><dd>{dp ? percent(dp.D) : "—"}</dd></div>
        <div><dt>Рнайб, місячний ПДВ</dt><dd>{dp ? uah(dp.PM) : "—"}</dd></div>
        <div><dt>Зупинені ПН/РК</dt><dd>{Array.isArray(stopped) ? stopped.filter((x) => x.status === 1).length : "—"}</dd></div>
      </dl>
      {last && (
        <p className="small">Позитивна історія ({monthShort(String(last.rdate).slice(0, 7))}): {marks.length
          ? `виконується ознак: ${marks.length} з 9 (${marks.map((k) => `№ ${k}`).join(", ")})`
          : "жодна ознака не виконується"}</p>
      )}
    </Block>
  );
}

// Чи вистачає даних для критерію 1: без повної історії з 2017 р. і всіх МД з СЕА число — розрахунок за завантаженим
function Crit1Completeness({ k, customsHref }) {
  const c = k.complete;
  if (!c || c.state === "complete") return null;
  const ddmm = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
  const items = [];
  if (c.history === "unknown") items.push("синк ЄРПН ще не завершувався — історія ПН/РК невідома");
  else if (c.history === "partial") items.push(`ПН/РК завантажено з ${ddmm(c.historyFrom)}, а критерій рахує з ${ddmm(c.requiredFrom)} — старіші придбання й продажі не враховано`);
  if (c.sea.missing) items.push(<>у СЕА ПДВ зареєстровано МД: {c.sea.md}, з них немає в eCabinet: {c.sea.missing} — ввезення за ними не враховано, доступне занижене. <a href={customsHref}>Завантажити МД →</a></>);
  if (c.sea.noGoods) items.push(`МД без розібраних товарів: ${c.sea.noGoods}`);
  if (c.noLines) items.push(`зареєстрованих ПН/РК без позицій: ${c.noLines}`);
  if (c.unknownStatus) items.push(`ПН/РК з невідомим станом реєстрації (не враховано): ${c.unknownStatus}`);
  return (
    <div className="notice warn-text" role="note">
      <p>Розрахунок за завантаженими даними, не підтверджений залишок:</p>
      <ul>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}

// Доступно для реєстрації ПН за кодами — критерій 1 СМКОР (ПКМУ № 1165), той самий розрахунок, що в Оракулі ПН:
// (придбання з ПДВ 20/14/7% з 2017 р. + ввезення за МД у СЕА) × 1,5 − уже продане, по групах УКТЗЕД (4 цифри) і класах ДКПП
function Crit1Block({ r, res, customsHref }) {
  const [all, setAll] = useState(false);
  const k = r.data?.crit1;
  const rows = k?.groups || [];
  const shown = all ? rows : rows.slice(0, 12);
  const max = Math.max(1, ...rows.map((x) => Math.abs(x.available)));
  const minus = rows.filter((x) => x.available < 0 && !x.inTable);
  const mult = String(k?.mult ?? 1.5).replace(".", ",");
  return (
    <section className="card block stock" aria-labelledby="crit1-h" aria-busy={r.loading}>
      <div className="block-head">
        <h2 id="crit1-h" className="block-title">Доступно для реєстрації ПН за кодами (критерій 1 СМКОР)</h2>
        {k && rows.length > 0 && <p className="stock-total">{minus.length ? <>у мінусі <b className="error">{minus.length}</b> з {rows.length}</> : <>груп <b>{rows.length}</b>, усі в плюсі</>}</p>}
      </div>
      <BlockState r={r} res={res} />
      {r.pending ? <div className="skeleton-lines" role="status"><span /><span /><span /><span className="sr-only">Завантаження…</span></div>
        : r.error && !r.data ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
        : !k || !rows.length ? <p className="empty">Даних для розрахунку ще немає — з'являться після синку ЄРПН.</p>
        : (
          <>
            <Crit1Completeness k={k} customsHref={customsHref} />
            <ul className="stock-list">
              {shown.map((x) => (
                <li key={x.id} className="stock-row"
                  title={`${x.label}${x.name ? ` — ${x.name}` : ""}: (придбано з ПДВ ${uah(x.bought)} + ввезено ${uah(x.imported)}) × ${mult} − продано ${uah(x.sold)}`}>
                  <span className="stock-label"><span className="mono-sm stock-code">{x.key}</span> {x.name || (x.kind === "goods" ? "група УКТЗЕД" : "")}
                    {x.table === "full" && <span className="badge badge-ok" title="Група у врахованій таблиці даних — критерій 1 не перевіряється"> таблиця даних</span>}
                    {x.table === "partial" && <span className="badge" title={`У врахованій таблиці даних лише коди ${x.tableCodes.join(", ")} — для решти кодів групи критерій 1 діє`}> таблиця: частково</span>}
                    {x.risky && <span className="badge badge-warn" title="Група з переліку для моніторингу (наказ ДПС № 67)"> перелік ДПС</span>}</span>
                  <span className="stock-track" aria-hidden="true">
                    <span className={`stock-bar${x.available < 0 ? " neg" : ""}`} style={{ width: `${Math.max(0.6, (Math.abs(x.available) / max) * 100)}%` }} />
                  </span>
                  <span className={`stock-value num${x.available < 0 && !x.inTable ? " error" : ""}`}>{uah(x.available)}</span>
                </li>
              ))}
            </ul>
            {rows.length > 12 && (
              <button type="button" className="btn btn-ghost btn-sm stock-more" onClick={() => setAll((v) => !v)} aria-expanded={all}>
                {all ? "Згорнути" : `Показати всі ${rows.length}`}
              </button>
            )}
            <p className="notice stock-note">
              Скільки ще можна продати без ПДВ за кожною групою, щоб ПН не потрапила під критерій 1: (придбання з ПДВ 20, 14 чи 7% з 2017 р.
              + ввезення за МД, відображеними в СЕА) × {mult} − уже видані ПН. Лише зареєстровані ПН/РК (зокрема за рішенням Комісії).
              Товари — групою за 4 цифрами УКТЗЕД (коди 1806 і 1806320000 — одна група), послуги — класом ДКПП. Сума округлена вниз до копійки.
              Це не загальний ліміт СЕА ПДВ (він один на компанію, у гривнях ПДВ). Мінус не означає автоматичне зупинення: ПН реєструється,
              якщо діє ознака безумовної реєстрації чи позитивна податкова історія. Перевірка конкретної ПН — Оракул у майстрі ПН.
              {k.tablesKnown ? "" : " Таблицю даних ще не синхронізовано (розділ СМКОР) — коди з неї не позначено."}
            </p>
          </>
        )}
    </section>
  );
}

// Залишки товарів по УКТЗЕД — як у дашборді etax
function StockBlock({ r, res }) {
  const [all, setAll] = useState(false);
  const d = r.data;
  const rows = d?.rows || [];
  const shown = all ? rows : rows.slice(0, 12);
  const max = Math.max(1, ...rows.map((x) => Math.abs(x.balanceVat)));
  return (
    <section className="card block stock" aria-labelledby="stock-h" aria-busy={r.loading}>
      <div className="block-head">
        <h2 id="stock-h" className="block-title">Залишки товарів з ПДВ по УКТЗЕД</h2>
        {d && rows.length > 0 && <p className="stock-total">разом <b>{uah(d.totalVat)}</b></p>}
      </div>
      <BlockState r={r} res={res} />
      {r.pending ? <div className="skeleton-lines" role="status"><span /><span /><span /><span className="sr-only">Завантаження…</span></div>
        : r.error && !d ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
        : !d.hasLines ? <p className="empty">Позицій ПН/РК ще немає — залишки з'являться після синку ЄРПН.</p>
        : !rows.length ? <p className="empty">За ПН/РК залишків немає: надходження й вибуття збігаються.</p>
        : (
          <>
            <ul className="stock-list">
              {shown.map((x) => (
                <li key={x.uktzed} className="stock-row" title={x.fullName}>
                  <span className="stock-label"><span className="mono-sm stock-code">{x.uktzed}</span> {x.name}</span>
                  <span className="stock-track" aria-hidden="true">
                    <span className={`stock-bar${x.balanceVat < 0 ? " neg" : ""}`} style={{ width: `${Math.max(0.6, (Math.abs(x.balanceVat) / max) * 100)}%` }} />
                  </span>
                  <span className={`stock-value num${x.balanceVat < 0 ? " error" : ""}`}>{uah(x.balanceVat)}</span>
                </li>
              ))}
            </ul>
            {rows.length > 12 && (
              <button type="button" className="btn btn-ghost btn-sm stock-more" onClick={() => setAll((v) => !v)} aria-expanded={all}>
                {all ? "Згорнути" : `Показати всі ${rows.length}`}
              </button>
            )}
            <p className="notice stock-note">
              Поточний стан, від періоду не залежить. Оцінка за позиціями ПН/РК{d.seaFiltered ? ", відображеними в СЕА" : ""}:
              отримані мінус видані{d.customs?.in_sea ? " плюс імпорт за МД" : ""}; з ПДВ — за ставками документів (звільнені й 0% — без ПДВ, МД — фактичний ПДВ).{" "}
              {!d.customs?.n ? "Митних декларацій не завантажено — імпорт не враховано."
                : d.customs.in_sea < d.customs.n ? `МД враховано ${d.customs.in_sea} з ${d.customs.n}: решта ще не відображена в реєстрі СЕА.`
                : `Враховано всі ${d.customs.n} МД.`}
            </p>
          </>
        )}
    </section>
  );
}
